(() => {
  'use strict';

  const COUNTRY_CODES = [
    'AD','AL','AM','AT','AZ','BA','BE','BG','BY','CH','CY','CZ','DE','DK','EE','ES','FI','FR','GB','GE','GR','HR','HU','IE','IS','IT','LI','LT','LU','LV','MC','MD','ME','MK','MT','NL','NO','PL','PT','RO','RS','RU','SE','SI','SK','SM','TR','UA','US','VA','XK'
  ];
  const PRIORITY_COUNTRIES = ['DE', 'FR', 'AT', 'GB', 'US', 'CH'];
  const FACTORS_TO_MG_L_CACO3 = {
    deg_dh: 17.848,
    deg_f: 10,
    mg_l_caco3: 1,
    ppm_caco3: 1,
    mmol_l: 100.0869,
    deg_e: 14.254,
    gpg: 17.118
  };
  const ALLOWED_CONTINUATION_FIELDS = [
    'country', 'postal_code', 'locality', 'location_id', 'official_id', 'supply_zone_id', 'street', 'district', 'state', 'address'
  ];
  const HIGH_CLASSIFICATIONS = new Set(['hard', 'very_hard', 'hard_to_very_hard']);
  const MEDIUM_CLASSIFICATIONS = new Set(['medium', 'slightly_hard', 'moderately_hard', 'moderately_soft']);

  const $ = (root, selector) => root.querySelector(selector);
  const $$ = (root, selector) => Array.from(root.querySelectorAll(selector));
  const clean = (value) => typeof value === 'string' ? value.trim() : '';
  const finite = (value) => {
    if (value === null || value === undefined || value === '' || typeof value === 'boolean') return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  };
  const sleep = (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));

  function readJson(root, selector) {
    try {
      return JSON.parse($(root, selector)?.textContent || '{}');
    } catch (_) {
      return {};
    }
  }

  function safeHttpsUrl(value) {
    try {
      const url = new URL(value, window.location.origin);
      return url.protocol === 'https:' ? url.href : '';
    } catch (_) {
      return '';
    }
  }

  function parseNumber(value) {
    if (typeof value === 'number') return Number.isFinite(value) && value >= 0 ? value : null;
    const normalized = String(value ?? '')
      .trim()
      .replace(/[\u00a0\u202f\s]/g, '')
      .replace(',', '.');
    if (!/^(?:\d+(?:\.\d*)?|\.\d+)$/.test(normalized)) return null;
    const number = Number(normalized);
    return Number.isFinite(number) && number >= 0 ? number : null;
  }

  function normalizeUkPostcode(value) {
    const compact = String(value ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (!/^(GIR0AA|(?:[A-PR-UWYZ][0-9][0-9A-HJKPSTUW]?|[A-PR-UWYZ][A-HK-Y][0-9][0-9ABEHMNPRVWXY]?)[0-9][ABD-HJLNP-UW-Z]{2})$/.test(compact)) return null;
    return `${compact.slice(0, -3)} ${compact.slice(-3)}`;
  }

  function normalizePostal(country, rawValue) {
    const value = clean(rawValue);
    if (!value) return { ok: false, key: 'postal_required' };
    if (country === 'DE') return /^\d{5}$/.test(value) ? { ok: true, value } : { ok: false, key: 'postal_invalid_de' };
    if (country === 'FR') return /^\d{5}$/.test(value) ? { ok: true, value } : { ok: false, key: 'postal_invalid_fr' };
    if (country === 'AT') return /^\d{4}$/.test(value) ? { ok: true, value } : { ok: false, key: 'postal_invalid_generic' };
    if (country === 'GB') {
      const postcode = normalizeUkPostcode(value);
      return postcode ? { ok: true, value: postcode } : { ok: false, key: 'postal_invalid_uk' };
    }
    if (country === 'US') {
      const match = value.match(/^\d{5}(?:-\d{4})?$/);
      return match ? { ok: true, value } : { ok: false, key: 'postal_invalid_us' };
    }
    return /^[\p{L}\p{N}][\p{L}\p{N}\s-]{1,18}$/u.test(value)
      ? { ok: true, value: value.toUpperCase() }
      : { ok: false, key: 'postal_invalid_generic' };
  }

  function classificationIntensity(classification, mgValue) {
    const code = clean(classification).toLowerCase();
    if (HIGH_CLASSIFICATIONS.has(code)) return 'high';
    if (MEDIUM_CLASSIFICATIONS.has(code)) return 'medium';
    if (code === 'soft' || code === 'very_soft') return 'low';
    if (Number.isFinite(mgValue)) {
      if (mgValue > 250) return 'high';
      if (mgValue >= 100) return 'medium';
    }
    return 'low';
  }

  function classifyManual(mgValue, country) {
    if (country === 'DE' || country === 'AT' || country === 'CH') {
      if (mgValue < 1.5 * FACTORS_TO_MG_L_CACO3.mmol_l) return 'soft';
      if (mgValue <= 2.5 * FACTORS_TO_MG_L_CACO3.mmol_l) return 'medium';
      return 'hard';
    }
    if (country === 'FR') {
      const degF = mgValue / FACTORS_TO_MG_L_CACO3.deg_f;
      if (degF < 10) return 'very_soft';
      if (degF < 20) return 'soft';
      if (degF <= 30) return 'moderately_hard';
      return 'hard_to_very_hard';
    }
    if (country === 'GB') {
      if (mgValue <= 100) return 'soft';
      if (mgValue <= 150) return 'slightly_hard';
      if (mgValue <= 200) return 'moderately_hard';
      if (mgValue <= 300) return 'hard';
      return 'very_hard';
    }
    if (mgValue < 60) return 'soft';
    if (mgValue <= 120) return 'medium';
    if (mgValue <= 180) return 'hard';
    return 'very_hard';
  }

  function init(root) {
    if (root.dataset.awpInitialized === 'true') return;
    root.dataset.awpInitialized = 'true';

    const translations = readJson(root, '[data-awp-translations]');
    const products = readJson(root, '[data-awp-products-json]');
    const endpoint = clean(root.dataset.endpoint);
    const locale = clean(root.dataset.locale) || document.documentElement.lang || 'de';
    const language = locale.split('-')[0].toLowerCase();
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    const workspace = $(root, '[data-awp-workspace]');
    const states = $$(root, '[data-awp-state]');
    const live = $(root, '[data-awp-live]');
    const lookupForm = $(root, '[data-awp-form="lookup"]');
    const choiceForm = $(root, '[data-awp-form="choice"]');
    const detailForm = $(root, '[data-awp-form="detail"]');
    const manualForm = $(root, '[data-awp-form="manual"]');
    const countryField = $(root, '[data-awp-country]');
    const postalField = $(root, '[data-awp-postal]');
    const postalLabel = $(root, '[data-awp-postal-label]');
    const choiceList = $(root, '[data-awp-choice-list]');
    const choiceTitle = $(root, '[data-awp-choice-title]');
    const choiceBody = $(root, '[data-awp-choice-body]');
    const detailTitle = $(root, '[data-awp-detail-title]');
    const detailBody = $(root, '[data-awp-detail-body]');
    const detailFieldContainer = $(root, '[data-awp-detail-field]');
    const detailPrivacy = $(root, '[data-awp-detail-privacy]');
    const manualRange = $(root, '[data-awp-manual-range]');
    const manualSingle = $(root, '[data-awp-manual-single]');
    const manualRangeFields = $(root, '[data-awp-manual-range-fields]');
    const manualIntro = $(root, '[data-awp-manual-intro]');
    const shareButton = $(root, '[data-awp-action="share"]');
    const shareLabel = $(root, '[data-awp-share-label]');

    const model = {
      payload: {},
      lastRequest: null,
      choiceField: null,
      detailFields: [],
      abortController: null,
      requestToken: 0,
      mode: 'automatic',
      shareData: null,
      shareLabelTimer: null
    };

    function t(path, fallback = '') {
      const value = path.split('.').reduce((current, key) => current && current[key], translations);
      return typeof value === 'string' && value ? value : fallback;
    }

    function interpolate(template, values = {}) {
      return String(template || '').replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_, key) => String(values[key] ?? ''));
    }

    function setShareLabel(value = t('share.label')) {
      if (shareLabel) shareLabel.textContent = value;
    }

    function resetShareFeedback() {
      window.clearTimeout(model.shareLabelTimer);
      model.shareLabelTimer = null;
      shareButton?.classList.remove('is-copied');
      setShareLabel();
    }

    function announce(message) {
      if (!live) return;
      live.textContent = '';
      window.requestAnimationFrame(() => { live.textContent = message || ''; });
    }

    function getShareUrl() {
      try {
        const url = new URL(window.location.href);
        url.search = '';
        url.hash = '';
        return url.href;
      } catch (_) {
        return String(window.location.href || '').split(/[?#]/, 1)[0];
      }
    }

    async function copyShareText(value) {
      if (navigator.clipboard?.writeText && window.isSecureContext) {
        await navigator.clipboard.writeText(value);
        return;
      }

      const textarea = document.createElement('textarea');
      textarea.value = value;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.inset = '0 auto auto -9999px';
      textarea.style.opacity = '0';
      document.body.append(textarea);
      textarea.focus();
      textarea.select();
      textarea.setSelectionRange(0, textarea.value.length);
      const copied = document.execCommand('copy');
      textarea.remove();
      if (!copied) throw new Error('copy_failed');
    }

    async function shareResult() {
      if (!model.shareData) return;

      const shareData = {
        title: t('share.title', document.title),
        text: interpolate(t('share.text'), model.shareData),
        url: getShareUrl()
      };

      if (typeof navigator.share === 'function') {
        try {
          if (!navigator.canShare || navigator.canShare(shareData)) {
            await navigator.share(shareData);
            return;
          }
        } catch (error) {
          if (error?.name === 'AbortError') return;
        }
      }

      try {
        await copyShareText(`${shareData.text}\n\n${shareData.url}`.trim());
        resetShareFeedback();
        shareButton?.classList.add('is-copied');
        setShareLabel(t('share.copied', t('share.label')));
        announce(t('share.copied_announcement', t('share.copied')));
        model.shareLabelTimer = window.setTimeout(resetShareFeedback, 2200);
      } catch (_) {
        announce(t('share.error'));
      }
    }

    function scrollToRoot() {
      const top = root.getBoundingClientRect().top + window.scrollY - 20;
      window.scrollTo({ top: Math.max(0, top), behavior: reducedMotion ? 'auto' : 'smooth' });
    }

    function showState(name, options = {}) {
      states.forEach((state) => {
        const active = state.dataset.awpState === name;
        state.hidden = !active;
        state.classList.toggle('is-active', active);
      });
      if (workspace) workspace.hidden = name === 'result';
      if (options.scroll !== false) scrollToRoot();
      if (options.focus !== false) {
        const target = states.find((state) => state.dataset.awpState === name);
        window.requestAnimationFrame(() => $(target || root, '[data-awp-focus-heading]')?.focus({ preventScroll: true }));
      }
    }

    function setError(name, message, field) {
      const error = $(root, `[data-awp-error="${name}"]`);
      if (error) {
        error.textContent = message;
        error.hidden = !message;
      }
      if (field) {
        if (message) field.setAttribute('aria-invalid', 'true');
        else field.removeAttribute('aria-invalid');
      }
      if (message) announce(`${t('accessibility.error_prefix')} ${message}`);
    }

    function clearErrors() {
      $$(root, '[data-awp-error]').forEach((error) => {
        error.hidden = true;
        error.textContent = '';
      });
      $$(root, '[aria-invalid="true"]').forEach((field) => field.removeAttribute('aria-invalid'));
    }

    function countryName(code) {
      try {
        return new Intl.DisplayNames([locale], { type: 'region' }).of(code) || code;
      } catch (_) {
        return code;
      }
    }

    function populateCountries() {
      const priorities = PRIORITY_COUNTRIES.filter((code) => COUNTRY_CODES.includes(code));
      const remaining = COUNTRY_CODES.filter((code) => !priorities.includes(code));
      const collator = new Intl.Collator(locale, { sensitivity: 'base' });
      remaining.sort((a, b) => collator.compare(countryName(a), countryName(b)));
      [...priorities, ...remaining].forEach((code) => {
        const option = document.createElement('option');
        option.value = code;
        option.textContent = countryName(code);
        countryField.append(option);
      });
      const desired = clean(root.dataset.defaultCountry).toUpperCase();
      countryField.value = COUNTRY_CODES.includes(desired) ? desired : 'DE';
      updateCountryUi();
    }

    function updateCountryUi() {
      const country = countryField.value;
      if (country === 'GB') {
        postalLabel.textContent = t('entry.postcode_label');
        postalField.placeholder = t('entry.postcode_placeholder');
        postalField.autocapitalize = 'characters';
      } else if (country === 'US') {
        postalLabel.textContent = t('entry.zip_code_label');
        postalField.placeholder = t('entry.zip_code_placeholder');
        postalField.autocapitalize = 'off';
      } else {
        postalLabel.textContent = t('entry.postal_code_label');
        postalField.placeholder = t('entry.postal_code_placeholder');
        postalField.autocapitalize = 'off';
      }
      const unitField = manualForm?.elements?.unit;
      if (unitField) {
        if (country === 'FR') unitField.value = 'deg_f';
        else if (country === 'GB') unitField.value = 'mg_l_caco3';
        else if (country === 'US') unitField.value = 'gpg';
        else unitField.value = 'deg_dh';
      }
      setError('country', '', countryField);
      setError('postal_code', '', postalField);
    }

    function mergeContinuation(response) {
      const contexts = [response?.continuation, response?.context, response?.request_context];
      contexts.forEach((context) => {
        if (!context || typeof context !== 'object') return;
        ALLOWED_CONTINUATION_FIELDS.forEach((field) => {
          if (context[field] !== undefined && context[field] !== null && context[field] !== '') model.payload[field] = context[field];
        });
      });
    }

    function normalizedFieldName(field, status) {
      const value = clean(field).toLowerCase();
      if (value === 'location' || value === 'municipality') return 'location_id';
      if (value === 'zone' || value === 'supply_zone') return 'supply_zone_id';
      if (value) return value;
      return status === 'location_choice_required' ? 'location_id' : 'locality';
    }

    function normalizeOptions(rawOptions) {
      return (Array.isArray(rawOptions) ? rawOptions : [])
        .map((option) => {
          if (typeof option === 'string' || typeof option === 'number') {
            const value = String(option);
            return { value, label: value };
          }
          if (!option || typeof option !== 'object') return null;
          const value = option.value ?? option.id ?? option.location_id ?? option.supply_zone_id ?? option.official_id ?? option.code ?? option.name ?? option.label;
          const label = option.label ?? option.name ?? option.locality ?? option.title ?? option.zone_name ?? option.value ?? value;
          return value !== undefined && label !== undefined ? { value: String(value), label: String(label) } : null;
        })
        .filter(Boolean);
    }

    function requiredConfig(response) {
      const required = response?.required_input || response?.required || {};
      const rawFields = Array.isArray(required.fields) ? required.fields : null;
      if (rawFields?.length) {
        return rawFields.map((field) => ({
          field: normalizedFieldName(field.field || field.name, response.status),
          label: clean(field.label),
          inputMode: clean(field.input_mode || field.inputMode) || (Array.isArray(field.options) ? 'select' : 'text'),
          options: normalizeOptions(field.options),
          placeholder: clean(field.placeholder)
        }));
      }
      const options = required.options || response?.choices || response?.options || response?.locations || response?.supply_zones || [];
      return [{
        field: normalizedFieldName(required.field || response?.required_field, response.status),
        label: clean(required.label),
        inputMode: clean(required.input_mode || required.inputMode) || (Array.isArray(options) && options.length ? 'select' : 'text'),
        options: normalizeOptions(options),
        placeholder: clean(required.placeholder)
      }];
    }

    function fieldCopy(field) {
      const key = ['street', 'district', 'locality', 'state', 'address'].includes(field) ? field : 'default';
      return {
        title: t(`detail.${key}_title`, t('detail.default_title')),
        body: t(`detail.${key}_body`, t('detail.default_body')),
        label: t(`detail.${key}_label`, key === 'street' ? t('detail.street_label') : key === 'locality' ? t('detail.locality_label') : ''),
        placeholder: t(`detail.${key}_placeholder`, key === 'street' ? t('detail.street_placeholder') : key === 'locality' ? t('detail.locality_placeholder') : '')
      };
    }

    function showChoice(response) {
      mergeContinuation(response);
      const [config] = requiredConfig(response);
      model.choiceField = config.field;
      const options = config.options.length ? config.options : normalizeOptions(response?.choices || response?.options);
      const isZone = config.field.includes('zone');
      choiceTitle.textContent = isZone ? t('choice.zone_title') : t('choice.location_title');
      choiceBody.textContent = isZone ? t('choice.zone_body') : t('choice.location_body');
      choiceList.replaceChildren();
      options.forEach((option, index) => {
        const label = document.createElement('label');
        label.className = 'aw-water-profile__choice';
        const input = document.createElement('input');
        input.type = 'radio';
        input.name = 'awp_choice';
        input.value = option.value;
        if (index === 0) input.checked = true;
        const text = document.createElement('span');
        text.textContent = option.label;
        label.append(input, text);
        choiceList.append(label);
      });
      if (!options.length) {
        showError('generic');
        return;
      }
      showState('choice');
      announce(t('accessibility.choice_required'));
    }

    function createDetailField(config, index) {
      const copy = fieldCopy(config.field);
      const wrapper = document.createElement('div');
      wrapper.className = 'aw-water-profile__field';
      const id = `${root.id}-detail-${index}`;
      const label = document.createElement('label');
      label.className = 'aw-water-profile__label';
      label.htmlFor = id;
      label.textContent = config.field === 'supply_zone_id' ? t('choice.zone_title') : (config.label || copy.label || config.field);
      let field;
      if (config.inputMode === 'select' || config.options.length) {
        field = document.createElement('select');
        field.className = 'aw-water-profile__select';
        const placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = t('detail.select_placeholder');
        field.append(placeholder);
        config.options.forEach((option) => {
          const item = document.createElement('option');
          item.value = option.value;
          item.textContent = option.label;
          field.append(item);
        });
      } else {
        field = document.createElement('input');
        field.className = 'aw-water-profile__input';
        field.type = 'text';
        field.placeholder = config.placeholder || copy.placeholder;
        field.maxLength = config.field === 'address' ? 220 : 160;
        if (config.field === 'street') field.autocomplete = 'address-line1';
        else if (config.field === 'locality') field.autocomplete = 'address-level2';
        else if (config.field === 'state') field.autocomplete = 'address-level1';
        else if (config.field === 'address') field.autocomplete = 'street-address';
        else field.autocomplete = 'off';
      }
      field.id = id;
      field.name = config.field;
      field.required = true;
      field.dataset.awpDetailInput = config.field;
      wrapper.append(label, field);
      return wrapper;
    }

    function showDetail(response) {
      mergeContinuation(response);
      const configs = requiredConfig(response);
      model.detailFields = configs;
      const primary = configs[0] || { field: 'locality' };
      const copy = fieldCopy(primary.field);
      detailTitle.textContent = copy.title;
      detailBody.textContent = copy.body;
      detailPrivacy.textContent = t('detail.privacy');
      detailFieldContainer.replaceChildren(...configs.map(createDetailField));
      showState('detail');
      announce(t('accessibility.choice_required'));
    }

    function showError(kind) {
      const map = {
        no_value: ['no_value_title', 'no_value_body'],
        temporary: ['temporary_title', 'temporary_body'],
        rate_limited: ['rate_limited_title', 'rate_limited_body'],
        generic: ['generic_title', 'generic_body']
      };
      const [titleKey, bodyKey] = map[kind] || map.generic;
      $(root, '[data-awp-error-title]').textContent = t(`states.${titleKey}`);
      $(root, '[data-awp-error-body]').textContent = t(`states.${bodyKey}`);
      showState('service_error');
    }

    async function resolve(payload, options = {}) {
      model.abortController?.abort();
      const controller = new AbortController();
      model.abortController = controller;
      const token = ++model.requestToken;
      model.lastRequest = { ...payload };
      model.payload = { ...payload };
      clearErrors();
      showState('loading');
      announce(t('accessibility.loading'));
      const started = performance.now();
      const timeout = window.setTimeout(() => controller.abort(), 22000);
      let response;
      let data;
      try {
        response = await fetch(endpoint, {
          method: 'POST',
          mode: 'cors',
          credentials: 'omit',
          cache: 'no-store',
          headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...payload, language }),
          signal: controller.signal
        });
        const text = await response.text();
        try { data = JSON.parse(text); } catch (_) { data = null; }
      } catch (error) {
        window.clearTimeout(timeout);
        if (token !== model.requestToken || error?.name === 'AbortError' && controller.signal.aborted && model.abortController !== controller) return;
        await sleep(Math.max(0, 460 - (performance.now() - started)));
        showError(error?.name === 'AbortError' ? 'temporary' : 'temporary');
        return;
      }
      window.clearTimeout(timeout);
      if (token !== model.requestToken) return;
      await sleep(Math.max(0, 460 - (performance.now() - started)));

      if (response.status === 429) {
        showError('rate_limited');
        return;
      }
      if (!data || !response.ok && data.ok !== true) {
        showError(response.status >= 500 ? 'temporary' : 'generic');
        return;
      }
      handleResponse(data);
    }

    function handleResponse(response) {
      const status = clean(response?.status).toLowerCase();
      if (status === 'profile' || status === 'regional_profile') {
        renderResult(response);
        return;
      }
      if (status === 'location_choice_required') {
        showChoice(response);
        return;
      }
      if (status === 'location_detail_required') {
        const configs = requiredConfig(response);
        if (configs.length === 1 && configs[0].options.length > 0 && configs[0].field.includes('location')) showChoice(response);
        else showDetail(response);
        return;
      }
      if (status === 'not_available' || status === 'not_resolved' || status === 'manual_required') {
        showError('no_value');
        return;
      }
      if (status === 'temporary_unavailable') {
        showError('temporary');
        return;
      }
      showError('generic');
    }

    function formatNumber(value, maximumFractionDigits = 2) {
      return new Intl.NumberFormat(locale, { maximumFractionDigits, minimumFractionDigits: 0 }).format(value);
    }

    function getDisplay(response) {
      const profile = response?.profile || {};
      const display = profile.display || {};
      return {
        value: finite(display.value ?? profile.value ?? profile.dh),
        minimum: finite(display.minimum ?? profile.minimum ?? profile.min),
        maximum: finite(display.maximum ?? profile.maximum ?? profile.max),
        unit: clean(display.unit || profile.unit || '°dH'),
        valueRole: clean(display.value_role || profile.value_role),
        officialAverage: finite(display.official_average ?? profile.official_average ?? profile.average_dh ?? profile.official_average_dh)
      };
    }

    function setDetail(name, value, asLink = '') {
      const row = $(root, `[data-awp-detail="${name}"]`);
      if (!row) return;
      const dd = row.querySelector('dd');
      row.hidden = !value;
      if (!value || !dd) return;
      dd.replaceChildren();
      if (asLink) {
        const link = document.createElement('a');
        link.className = 'aw-water-profile__source-link';
        link.href = asLink;
        link.target = '_blank';
        link.rel = 'external noopener noreferrer';
        link.textContent = value;
        dd.append(link);
      } else {
        dd.textContent = value;
      }
    }

    function resultPrecision(profile, response) {
      const precision = clean(profile?.coverage_precision || profile?.precision || response?.coverage?.level).toLowerCase();
      const map = {
        postal_code: 'precision_postal_code',
        locality: 'precision_locality',
        municipality: 'precision_locality',
        district: 'precision_district',
        street: 'precision_street',
        supply_zone: 'precision_supply_zone',
        regional: 'precision_regional'
      };
      return t(`result.${map[precision] || 'precision_regional'}`);
    }

    function freshnessText(profile) {
      const freshness = clean(profile?.freshness).toLowerCase();
      if (freshness === 'current') return t('result.freshness_current');
      if (freshness === 'older' || freshness === 'stale') return t('result.freshness_older');
      return t('result.freshness_unknown');
    }

    function normalizedMg(profile, display) {
      const fromProfile = finite(profile?.normalized?.mg_l_caco3 ?? profile?.mg_l_caco3);
      if (fromProfile !== null) return fromProfile;
      const representative = display.value ?? display.officialAverage ?? (
        display.minimum !== null && display.maximum !== null ? (display.minimum + display.maximum) / 2 : null
      );
      if (representative === null) return null;
      const unit = display.unit.toLowerCase();
      if (unit.includes('°dh')) return representative * FACTORS_TO_MG_L_CACO3.deg_dh;
      if (unit.includes('°f')) return representative * FACTORS_TO_MG_L_CACO3.deg_f;
      if (unit.includes('mmol')) return representative * FACTORS_TO_MG_L_CACO3.mmol_l;
      if (unit.includes('gpg') || unit.includes('grain')) return representative * FACTORS_TO_MG_L_CACO3.gpg;
      if (unit.includes('°e') || unit.includes('clark')) return representative * FACTORS_TO_MG_L_CACO3.deg_e;
      return representative;
    }

    function renderProducts(intensity) {
      const container = $(root, '[data-awp-products]');
      const context = $(root, '[data-awp-products-context]');
      const selection = {
        low: ['zeitanmut', 'koerperlicht', 'seidenthron'],
        medium: ['zeitanmut', 'seelenbad', 'seidenkrone'],
        high: ['zeitanmut', 'hautfrieden', 'seidenkrone']
      }[intensity] || ['zeitanmut', 'seelenbad', 'seidenkrone'];
      const fallback = ['tagesnacht', 'hautfrieden', 'koerperlicht', 'seidenthron', 'seelenbad', 'seidenkrone'];
      const chosen = [];
      [...selection, ...fallback].forEach((key) => {
        const product = products[key];
        if (!product || chosen.some((item) => item.key === key) || !clean(product.url)) return;
        if (chosen.length < 3) chosen.push({ key, product });
      });
      context.textContent = t(`products.context_${intensity}`);
      container.replaceChildren();
      chosen.forEach(({ key, product }) => {
        const card = document.createElement('a');
        card.className = 'aw-water-profile__recommendation';
        card.href = product.url;
        if (product.available === false) card.classList.add('is-unavailable');

        const media = document.createElement('div');
        media.className = 'aw-water-profile__recommendation-media';
        if (clean(product.image)) {
          const image = document.createElement('img');
          image.src = product.image;
          image.alt = clean(product.name || product.shortName);
          image.loading = 'lazy';
          image.decoding = 'async';
          image.width = 900;
          image.height = 1035;
          media.append(image);
        } else {
          const placeholder = document.createElement('span');
          placeholder.className = 'aw-water-profile__recommendation-placeholder';
          placeholder.textContent = '◇';
          media.append(placeholder);
        }

        const content = document.createElement('div');
        content.className = 'aw-water-profile__recommendation-content';
        const kicker = document.createElement('p');
        kicker.className = 'aw-water-profile__recommendation-kicker';
        kicker.textContent = clean(product.type) || 'ANTLITZWERK';
        const name = document.createElement('h3');
        name.className = 'aw-water-profile__recommendation-name';
        name.textContent = clean(product.shortName || product.name);
        const type = document.createElement('p');
        type.className = 'aw-water-profile__recommendation-type';
        type.textContent = clean(product.type);
        type.hidden = !type.textContent;
        const reason = document.createElement('p');
        reason.className = 'aw-water-profile__recommendation-reason';
        const reasons = translations?.products?.reasons?.[key] || {};
        reason.textContent = reasons[intensity] || reasons.default || '';
        const footer = document.createElement('div');
        footer.className = 'aw-water-profile__recommendation-footer';
        const price = document.createElement('span');
        price.className = 'aw-water-profile__recommendation-price';
        price.textContent = clean(product.price);
        const cta = document.createElement('span');
        cta.className = 'aw-water-profile__recommendation-cta';
        const ctaText = document.createElement('span');
        ctaText.textContent = product.available === false ? t('products.unavailable') : t('products.view_product');
        const arrow = document.createElement('span');
        arrow.textContent = '→';
        arrow.setAttribute('aria-hidden', 'true');
        cta.append(ctaText, arrow);
        footer.append(price, cta);
        content.append(kicker, name, type, reason, footer);
        card.append(media, content);
        container.append(card);
      });
    }

    function renderResult(response) {
      const profile = response?.profile || {};
      const display = getDisplay(response);
      const mgValue = normalizedMg(profile, display);
      const broadClassification = clean(profile.classification || classifyManual(mgValue ?? 0, response.country || model.payload.country)).toLowerCase() || 'medium';
      const displayClassification = clean(profile.local_classification).toLowerCase() || broadClassification;
      const intensity = classificationIntensity(broadClassification, mgValue);
      const classificationLabel = t(`classifications.${displayClassification}`, displayClassification.replaceAll('_', ' '));
      const classificationElement = $(root, '[data-awp-classification]');
      classificationElement.textContent = classificationLabel;
      const labelLength = classificationLabel.trim().length;
      classificationElement.dataset.awpClassificationSize = labelLength > 23
        ? 'extra-long'
        : labelLength > 16
          ? 'long'
          : labelLength > 10
            ? 'medium'
            : 'short';

      let valueText = '';
      const hasRange = display.minimum !== null && display.maximum !== null && display.maximum !== display.minimum;
      if (hasRange) valueText = `${formatNumber(display.minimum)}–${formatNumber(display.maximum)} ${display.unit}`;
      else if (display.value !== null) valueText = `${formatNumber(display.value)} ${display.unit}`;
      else if (display.minimum !== null) valueText = `${formatNumber(display.minimum)} ${display.unit}`;
      $(root, '[data-awp-primary-value]').textContent = valueText;
      model.shareData = { classification: classificationLabel, value: valueText };
      resetShareFeedback();

      const average = $(root, '[data-awp-average]');
      const averageValue = display.officialAverage ?? (display.valueRole === 'official_average' ? display.value : null);
      if (averageValue !== null && hasRange) {
        average.textContent = `${t('result.official_average')}: ${formatNumber(averageValue)} ${display.unit}`;
        average.hidden = false;
      } else {
        average.hidden = true;
        average.textContent = '';
      }

      const isManual = response?.source?.provider === 'manual_entry' || model.mode === 'manual';
      const isRegional = response.status === 'regional_profile' || clean(profile.value_confidence).includes('regional');
      $(root, '[data-awp-result-provenance]').textContent = isManual
        ? t('result.entered_value')
        : hasRange || isRegional ? t('result.published_area_range') : t('result.published_supply');

      const marker = $(root, '[data-awp-scale-marker]');
      const markerPercent = Number.isFinite(mgValue) ? Math.min(96, Math.max(4, (mgValue / 400) * 100)) : 50;
      marker?.style.setProperty('--awp-marker', `${markerPercent}%`);

      const location = response?.location || {};
      const locationParts = [location.locality, location.postal_code, location.district].map(clean).filter((value, index, array) => value && array.indexOf(value) === index);
      setDetail('location', locationParts.join(' · '));
      setDetail('zone', clean(profile.supply_zone || profile.waterwork || response?.coverage?.level));
      const sourceUrl = safeHttpsUrl(response?.source?.url);
      setDetail('source', clean(response?.source?.name || response?.source?.domain), sourceUrl);
      const dated = clean(profile.source_date || profile.source_year || profile.source_period);
      setDetail('updated', dated || freshnessText(profile));
      setDetail('confidence', isManual ? t('result.confidence_manual') : resultPrecision(profile, response));

      const interpretation = translations?.interpretation?.[intensity] || translations?.interpretation?.medium || {};
      ['meaning', 'skin', 'hair'].forEach((key) => {
        const element = $(root, `[data-awp-interpretation="${key}"]`);
        if (element) element.textContent = interpretation[key] || '';
      });

      const variation = $(root, '[data-awp-variation]');
      if (variation) variation.hidden = !hasRange;
      const areaDisclaimer = $(root, '[data-awp-area-disclaimer]');
      if (areaDisclaimer) areaDisclaimer.hidden = !isRegional;
      const softenerRegional = $(root, '[data-awp-softener-regional-note]');
      if (softenerRegional) softenerRegional.hidden = !isRegional;

      renderProducts(intensity);
      model.mode = isManual ? 'manual' : 'automatic';
      showState('result');
      announce(t('accessibility.result_ready'));
    }

    function openManual(well = false) {
      model.mode = 'manual';
      const title = $(root, '[data-awp-state="manual"] [data-awp-focus-heading]');
      title.textContent = well ? t('manual.well_title') : t('manual.title');
      manualIntro.textContent = well ? t('manual.well_body') : t('manual.intro');
      showState('manual');
      announce(t('accessibility.manual_opened'));
    }

    function reset() {
      model.abortController?.abort();
      model.payload = {};
      model.lastRequest = null;
      model.choiceField = null;
      model.detailFields = [];
      model.mode = 'automatic';
      model.shareData = null;
      resetShareFeedback();
      lookupForm.reset();
      manualForm.reset();
      countryField.value = COUNTRY_CODES.includes(clean(root.dataset.defaultCountry).toUpperCase()) ? clean(root.dataset.defaultCountry).toUpperCase() : 'DE';
      manualRangeFields.hidden = true;
      manualSingle.hidden = false;
      updateCountryUi();
      clearErrors();
      showState('entry');
    }

    lookupForm.addEventListener('submit', (event) => {
      event.preventDefault();
      clearErrors();
      const country = clean(countryField.value).toUpperCase();
      if (!country) {
        setError('country', t('validation.country_required'), countryField);
        countryField.focus();
        return;
      }
      const postal = normalizePostal(country, postalField.value);
      if (!postal.ok) {
        setError('postal_code', t(`validation.${postal.key}`), postalField);
        postalField.focus();
        return;
      }
      postalField.value = postal.value;
      resolve({ country, postal_code: postal.value });
    });

    choiceForm.addEventListener('submit', (event) => {
      event.preventDefault();
      const selected = choiceForm.querySelector('input[name="awp_choice"]:checked');
      if (!selected) {
        setError('choice', t('validation.choice_required'));
        return;
      }
      setError('choice', '');
      const nextPayload = { ...model.payload, [model.choiceField]: selected.value };
      resolve(nextPayload);
    });

    detailForm.addEventListener('submit', (event) => {
      event.preventDefault();
      const nextPayload = { ...model.payload };
      /** @type {HTMLInputElement | HTMLSelectElement | null} */
      let firstInvalid = null;
      model.detailFields.forEach((config) => {
        /** @type {HTMLInputElement | HTMLSelectElement | null} */
        const field = detailForm.querySelector(`[data-awp-detail-input="${CSS.escape(config.field)}"]`);
        const value = clean(field?.value);
        if (!value && !firstInvalid) firstInvalid = field;
        if (value) nextPayload[config.field] = value;
      });
      if (firstInvalid) {
        setError('detail', t('validation.detail_required'), firstInvalid);
        firstInvalid.focus();
        return;
      }
      setError('detail', '');
      resolve(nextPayload);
    });

    manualRange.addEventListener('change', () => {
      manualRangeFields.hidden = !manualRange.checked;
      manualSingle.hidden = manualRange.checked;
      setError('manual', '');
    });

    manualForm.addEventListener('submit', (event) => {
      event.preventDefault();
      setError('manual', '');
      const formData = new FormData(manualForm);
      const unitKey = clean(formData.get('unit'));
      const factor = FACTORS_TO_MG_L_CACO3[unitKey];
      const isRange = manualRange.checked;
      const value = parseNumber(formData.get('value'));
      const minimum = parseNumber(formData.get('minimum'));
      const maximum = parseNumber(formData.get('maximum'));
      if (!factor || (!isRange && value === null) || (isRange && (minimum === null || maximum === null))) {
        setError('manual', t('validation.value_invalid'));
        return;
      }
      if (isRange && maximum < minimum) {
        setError('manual', t('validation.range_order'));
        return;
      }
      const representative = isRange ? (minimum + maximum) / 2 : value;
      const mgValue = representative * factor;
      const country = clean(countryField.value).toUpperCase() || 'DE';
      const classification = classifyManual(mgValue, country);
      const unitLabels = { deg_dh: '°dH', deg_f: '°f / TH', mg_l_caco3: 'mg/l CaCO₃', ppm_caco3: 'ppm CaCO₃', mmol_l: 'mmol/l', deg_e: '°e / Clark', gpg: 'gpg' };
      const response = {
        ok: true,
        status: isRange ? 'regional_profile' : 'profile',
        country,
        location: { country, postal_code: clean(postalField.value), locality: null },
        profile: {
          presentation: isRange ? 'regional_range' : 'single',
          display: {
            value: isRange ? null : value,
            minimum: isRange ? minimum : null,
            maximum: isRange ? maximum : null,
            unit: unitLabels[unitKey],
            value_role: isRange ? 'user_entered_range' : 'user_entered'
          },
          normalized: { mg_l_caco3: mgValue, dh: mgValue / FACTORS_TO_MG_L_CACO3.deg_dh, mmol_l: mgValue / FACTORS_TO_MG_L_CACO3.mmol_l },
          classification,
          value_confidence: 'user_supplied',
          coverage_precision: 'tap_or_user_value',
          freshness: 'user_supplied'
        },
        source: { name: t('result.entered_value'), provider: 'manual_entry', official: false }
      };
      renderResult(response);
    });

    countryField.addEventListener('change', updateCountryUi);
    root.addEventListener('click', (event) => {
      const action = event.target.closest('[data-awp-action]')?.dataset.awpAction;
      if (!action) return;
      if (action === 'open-manual') openManual(false);
      else if (action === 'open-well') openManual(true);
      else if (action === 'back' || action === 'back-to-entry') showState('entry');
      else if (action === 'retry' && model.lastRequest) resolve(model.lastRequest);
      else if (action === 'share') void shareResult();
      else if (action === 'reset') reset();
    });

    populateCountries();
  }

  function boot() {
    document.querySelectorAll('[data-awp-root]').forEach(init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();

  document.addEventListener('shopify:section:load', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    target?.querySelectorAll('[data-awp-root]').forEach(init);
  });
})();
